import type { DesignNode } from '@repo/api-contracts';
import { apply, IDENTITY, invert, type Matrix, multiply } from './matrix';
import { nodeBoxOf, nodeMatrix } from './node-transform';

export interface NodeHit {
  readonly nodeId: string;
  /** 바깥 그룹부터 안쪽 그룹까지의 id. 최상위 노드면 빈 배열. */
  readonly groupPath: readonly string[];
}

export interface PointMm {
  readonly xMm: number;
  readonly yMm: number;
}

const hitNodes = (
  layers: readonly DesignNode[],
  point: PointMm,
  parent: Matrix,
  groupPath: readonly string[],
): NodeHit | null => {
  for (let index = layers.length - 1; index >= 0; index -= 1) {
    const node = layers[index];
    if (node === undefined) continue;
    const matrix = multiply(parent, nodeMatrix(node));
    if (node.type === 'group') {
      const hit = hitNodes(node.children, point, matrix, [...groupPath, node.id]);
      if (hit !== null) return hit;
      continue;
    }
    const inverse = invert(matrix);
    const box = nodeBoxOf(node);
    if (inverse === null || box === null) continue;
    const local = apply(inverse, { x: point.xMm, y: point.yMm });
    if (local.x >= 0 && local.x <= box.widthMm && local.y >= 0 && local.y <= box.heightMm) {
      return { nodeId: node.id, groupPath };
    }
  }
  return null;
};

/** 위 레이어부터(배열 뒤부터) 회전·플립을 역적용해 상자 포함을 본다(D13). 그룹은 자식을 먼저 본다. */
export const hitTestLayers = (
  layers: readonly DesignNode[],
  point: PointMm,
  parent: Matrix = IDENTITY,
): NodeHit | null => hitNodes(layers, point, parent, []);
